import { createSlice } from '@reduxjs/toolkit';

import { Project, Unit, ActionItem, Process, Lease, FeedEvent } from '@urbanmix-tech/shared-js';

import { ColorGradientVisualizer } from '@urbanmix-tech/um-embedded';

interface SelectedUnit {
  apartmentId: string;
  isActive: boolean;
  level: number;
  line: string;
  popup: string | null;
  popupId: string;
}

export interface ProjectState {
  project: Project;
  projectUnits: Unit[];
  projectLeases: Lease[];
  projectApartments: SelectedUnit[];
  apartmentData: [] | null;
  actionItems: ActionItem[] | [];
  selectedUnit: SelectedUnit | null;
  highlightedUnits : SelectedUnit[] | null;
  selectedUnitData: Unit | null;
  unitMixSelectedColumn: string | undefined;
  unitMixSelectedColumnState: boolean;
  isApartmentView: boolean;
  modelValues: any;
  projectVisualizer: any;
  hoveredActionItem: ActionItem | null;
  hoveredUnitId : string | null;
  processes: Process[] | [];
  draftLeases: Lease[] | [];
  /**
   * The project's `state_*` feed events — the append-only log unit status is
   * folded from. Loaded once per project rather than per tab; read through
   * `selectUnitStatusById` in `unitStatusSelectors`.
   */
  unitStatusEvents: FeedEvent[];
}

const initialState: ProjectState = {
  project: {} as Project,
  projectUnits: [],
  projectLeases: [],
  projectApartments: [],
  actionItems: [],
  selectedUnit: {} as SelectedUnit | null,
  highlightedUnits: null,
  selectedUnitData: {} as Unit | null,
  unitMixSelectedColumn: undefined,
  unitMixSelectedColumnState: false,
  apartmentData : null,
  isApartmentView: false,
  modelValues: [],
  projectVisualizer: new ColorGradientVisualizer(
    ['#313695', '#FFFFE5', '#A50026'],
    3500,
    7000
  ),
  hoveredActionItem: null,
  hoveredUnitId : null,
  processes: [],
  draftLeases: [],
  unitStatusEvents: [],
};

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProjectData: (state, action) => {
      state.project = action.payload;
    },
    setProjectUnits: (state, action) => {
      state.projectUnits = action.payload;
    },
    setProjectLeases: (state, action) => {
      state.projectLeases = action.payload;
    },
    setActionItems: (state, action) => {
      state.actionItems = action.payload;
    },
    setSelectedUnit: (state, action) => {
      console.log('set selected unit action.payload', action.payload);
      state.selectedUnit = action.payload;
    },
    setSelectedUnitData: (state, action) => {      
      state.selectedUnitData = action.payload;
    },
    setHighlightedUnits: (state, action) => {
      state.highlightedUnits = action.payload;
    },
    setProjectApartments: (state, action) => {
      state.projectApartments = action.payload;
    },
    setApartmentData: (state, action) => {
      state.apartmentData = action.payload; 
    },
    setIsApartmentView: (state, action) => {
      state.isApartmentView = action.payload;
    },
    setVisualizer: (state, action) => {
      state.projectVisualizer = action.payload;
    },
    setBuildingVisual: (state, action) => {
      state.apartmentData = action.payload.apartmentData;
      state.projectVisualizer = action.payload.visualizer
    },
    setHoveredActionItem: (state, action) => {
      state.hoveredActionItem = action.payload;
    },
    setHoveredUnitId: (state, action) => {
      state.hoveredUnitId = action.payload;
    },
    setProcesses: (state, action) => {
      state.processes = action.payload;
    },
    addDraftLease: (state, action) => {
      state.draftLeases = [...state.draftLeases, action.payload];
    },
    removeDraftLease: (state, action) => {
      state.draftLeases = state.draftLeases.filter(lease => lease.id !== action.payload);
    },
    setDraftLeases: (state, action) => {
      state.draftLeases = action.payload;
    },
    setUnitStatusEvents: (state, action) => {
      state.unitStatusEvents = action.payload;
    }
  },
});

export const {
  setProjectData,
  setProjectUnits,
  setProjectLeases,
  setActionItems,
  setSelectedUnit,
  setSelectedUnitData,
  setVisualizer,
  setIsApartmentView,
  setApartmentData,
  setHighlightedUnits,
  setProjectApartments,
  setBuildingVisual,
  setHoveredUnitId,
  setHoveredActionItem,
  setProcesses,
  addDraftLease,
  removeDraftLease,
  setDraftLeases,
  setUnitStatusEvents
} = projectSlice.actions;

export default projectSlice.reducer;
